import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { UserDetail } from '../types';

const PROTECTED_ADMIN_EMAIL = 'justus.wiedemann@celsios.io';

export function DangerPanel({ detail }: { detail: UserDetail }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const isProtected = detail.user.email === PROTECTED_ADMIN_EMAIL;

  const forceLogout = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-force-logout', {
        body: { target_user_id: detail.user.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: 'Tokens revoked',
        description: 'User will need to re-authenticate Microsoft Graph in the Add-In.',
      });
    } catch (err) {
      toast({
        title: 'Failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Warning banner */}
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-destructive">Danger zone</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            These actions affect this user's session and cannot easily be undone.
          </div>
        </div>
      </div>

      {/* Action card */}
      <div className="rounded-xl border border-border/60 bg-card p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-9 w-9 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
            <LogOut className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium">Force logout (revoke Graph tokens)</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Deletes the user's stored Microsoft Graph tokens. The Add-In will prompt them to sign
              in again.
            </div>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={busy || isProtected}>
              Force logout
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Force logout {detail.user.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                The user's Microsoft Graph tokens will be deleted. They will need to re-authenticate
                in the Add-In on next use.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={forceLogout}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Force logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
